import { FC, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useClerk, useUser } from '@clerk/clerk-react'

// COMPONENTS
import * as Icons from '@icons'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import Spinner from '@components/atoms/spinner'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { VirtualCardAPI } from '@apis'
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { getErrorMessage } from '@utils/error-helper'

import './delete-account.css'

const DeleteAccountScreen: FC = () => {
    const navigate = useNavigate()
    const { user } = useUser()
    const { signOut } = useClerk()
    const { createToast } = useFixedSettings()
    const confirmRef = useRef<HTMLDialogElement>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        navigate('../', { replace: true })
    }, [navigate])

    function openConfirmDialog() {
        confirmRef.current?.showModal()
    }

    function closeConfirmDialog() {
        if (isDeleting) return
        confirmRef.current?.close()
    }

    async function handleDeleteAccount() {
        try {
            if (!user) throw new Error('Auth Services have yet to load.')

            setIsDeleting(true)
            await VirtualCardAPI.UpdateDeletionRequest.fetch(user.id)
            await signOut({
                redirectUrl: '/sign-in'
            })
        } catch (error: unknown) {
            console.error(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to request account deletion right now.')
            })
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <div hidden>
            <dialog ref={confirmRef} onCancel={closeConfirmDialog}>
                <div>
                    <div className='delete-account-confirm-dialog'>
                        <h2>Please Confirm Account Deletion Request</h2>
                        <p>
                            <strong>Your account will be scheduled for deletion in 30 days.</strong>
                        </p>
                        <p>
                            If you sign in again before then, your deletion request will be canceled.
                        </p>
                        {isDeleting
                            ? (
                                <div className='flex flex-col items-center justify-center gap-4'>
                                    <Spinner indicator='circle' />
                                    <p>Scheduling deletion...</p>
                                </div>
                            ) : (
                                <div className='flex flex-col gap-3'>
                                    <Button type='button' theme='primary' className='delete-account-button' onClick={handleDeleteAccount}>
                                        Request Account Deletion
                                    </Button>
                                    <Button type='button' theme='secondary' onClick={closeConfirmDialog}>
                                        Cancel
                                    </Button>
                                </div>
                            )
                        }
                    </div>
                </div>
            </dialog>

            <div className='flex flex-row items-center gap-4'>
                <a onClick={() => navigate('../')}>
                    <img src='/icons/arrow-left.svg' alt='back' style={{ width: '1.4rem', height: '1.4rem' }} draggable='false' />
                </a>
                <h1>Delete Account</h1>
            </div>

            <Card className='gap-4 delete-account-card'>
                <div className='delete-account-heading-row'>
                    <div className='delete-account-warning-icon'>
                        <Icons.WarningSmall tint='var(--splitcard-red-600)' width='1.5rem' height='1.5rem' />
                    </div>
                    <h4>Request deletion of your Splitcard account</h4>
                </div>
                <div className='flex flex-col gap-3'>
                    <p>
                        If you continue, your Splitcard account and profile data will be scheduled for deletion in 30 days.
                    </p>
                    <p>
                        If you sign back in during that 30-day period, the deletion request will be canceled and your account will remain active.
                    </p>
                    <p>
                        Any outstanding charges or refunds will still be finalized even if your account is later deleted.
                    </p>
                    <p>
                        <strong>After 30 days, deletion is permanent and can&apos;t be undone.</strong>
                    </p>
                </div>
            </Card>

            <Button
                type='button'
                className='delete-account-button'
                onClick={openConfirmDialog}
            >
                Request Account Deletion
            </Button>
        </div>
    )
}

const DeleteAccountRoute: RouteObject = {
    path: 'delete-account',
    Component: DeleteAccountScreen,
}

export { DeleteAccountRoute }
